/**
 * market-intel.js — Free Market Intelligence Aggregator for SPEKTRX
 *
 * Fetches and caches market data from free sources:
 * - CoinGecko: global crypto market cap, BTC dominance, trending coins
 * - Fear & Greed Index: crypto market sentiment (alternative.me)
 * - Yahoo Finance RSS: news headlines for portfolio tickers
 * - Internal: FX trends, portfolio-derived market context
 *
 * All data cached in memory with configurable TTL to minimize API calls.
 * No API keys required — 100% free tier.
 *
 * Exports:
 * - getMarketIntel(topics)  — main aggregator, returns cached or fresh data
 * - refreshMarketIntel()    — force-refresh all sources
 * - getMarketSummary()      — quick text summary for AI context
 */

import { S } from '../core/state.js';
import { fxUSDTRY, fxEURTRY } from '../core/utils.js';

// ─── CACHE ───────────────────────────────────────────────────

const _cache = {};
const TTL = {
  cryptoGlobal: 5 * 60 * 1000,   // 5 min
  fearGreed: 30 * 60 * 1000,     // 30 min (updates every 24h anyway)
  trending: 10 * 60 * 1000,      // 10 min
  news: 15 * 60 * 1000,          // 15 min
};

function _isFresh(key) {
  const entry = _cache[key];
  if (!entry) return false;
  return (Date.now() - entry.ts) < (TTL[key] || 300000);
}

function _set(key, data) {
  _cache[key] = { data, ts: Date.now() };
}

function _get(key) {
  return _cache[key]?.data || null;
}

// ─── API CALLS ───────────────────────────────────────────────

/** Fetch via Netlify proxy (handles CORS) */
async function _proxyFetch(source, params = '') {
  const base = '/api/market-intel';
  const url = `${base}?source=${source}${params ? '&' + params : ''}`;
  const res = await fetch(url, { signal: AbortSignal.timeout(10000) });
  if (!res.ok) throw new Error(`Proxy ${source}: ${res.status}`);
  return res.json();
}

/**
 * CoinGecko global — through the proxy, always.
 *
 * This used to try the browser first, on the comment "CoinGecko supports CORS". It does not,
 * for the free endpoint, and has not for a while: every refresh produced two red console
 * lines before silently falling through to the proxy that was going to answer anyway.
 *
 *   Access to fetch at 'https://api.coingecko.com/api/v3/global' from origin
 *   'https://xzagx.netlify.app' has been blocked by CORS policy
 *   GET https://api.coingecko.com/api/v3/global net::ERR_FAILED 429 (Too Many Requests)
 *
 * An optimisation whose premise stopped being true is not a fast path, it is a guaranteed
 * error. And guaranteed errors are how a real one hides: the BIST outage on 2026-08-03 sat in
 * this same console for an unknown number of days. Same reasoning as /api/bist and /api/tefas
 * — if the browser cannot read it, do not ask the browser. (audit 2026-08-03)
 */
async function _fetchCryptoGlobal() {
  if (_isFresh('cryptoGlobal')) return _get('cryptoGlobal');

  try {
    const result = await _proxyFetch('crypto-global');
    if (result) _set('cryptoGlobal', result);
    return result;
  } catch (_e) { return _get('cryptoGlobal'); }
}

/** CoinGecko trending — through the proxy, for the same reason as the global endpoint. */
async function _fetchTrending() {
  if (_isFresh('trending')) return _get('trending');

  try {
    const result = await _proxyFetch('trending');
    if (result) _set('trending', result);
    return result;
  } catch (_e) { return _get('trending'); }
}

/** Fear & Greed Index (via proxy — alternative.me doesn't support CORS) */
async function _fetchFearGreed() {
  if (_isFresh('fearGreed')) return _get('fearGreed');

  try {
    const result = await _proxyFetch('fear-greed');
    if (result) _set('fearGreed', result);
    return result;
  } catch (_e) { return _get('fearGreed'); }
}

/** News headlines for given tickers (via proxy — Yahoo RSS) */
async function _fetchNews(tickers) {
  const key = 'news';
  if (_isFresh(key) && _get(key)?.length) return _get(key);

  try {
    const result = await _proxyFetch('news', `tickers=${encodeURIComponent(tickers)}`);
    if (result?.length) _set(key, result);
    return result || [];
  } catch (_e) { return _get(key) || []; }
}

// ─── INTERNAL MARKET CONTEXT ─────────────────────────────────

/** Derive market context from app state (no API call needed) */
function _getInternalContext() {
  // This object is read straight into the Copilot and Digest prompts as "USD/TRY=<n>".
  // `|| 1` told the model the lira was at parity. null makes the consumers omit the line.
  const fxR = fxUSDTRY();
  const fxE = fxEURTRY();
  const _xauTRYPerGram = (S.prices && S.prices.XAU && S.prices.XAU.price) || 0;
  const _trInflation = (S.settings && S.settings.trInflation) || 0;

  // FX overview
  const fx = {
    USDTRY: fxR,
    EURTRY: fxE,
    // ⚠ THE SYNC-002 SIBLING THAT WAS MISSED, in the same object literal. `goldTRYPerGram` and
    // `inflationPctAnnual` two lines down already say 'unavailable' rather than 0, for exactly
    // this reason — a RATE reported as zero is not a missing rate, it is a claim that the euro
    // is worthless, and the model reasons over it. UI-004, measured 2026-08-17.
    EURUSD: (fxR > 0 && fxE > 0) ? +(fxE / fxR).toFixed(4) : 'unavailable',
    // ⚠ SYNC-002 — TWO FIELDS NOTHING HAS EVER WRITTEN, SENT AS ZERO UNDER A HEADING
    // THAT SAYS "LIVE".
    //
    // `S.fxRateXAU` appeared in the entire repository exactly once: on this line. `S.inflation`
    // was consolidated into `S.settings.trInflation` by v46 — `state.js` says so in as many
    // words, *"trInflation lives HERE and only here"* — so the top-level name has been dead
    // since. Both `|| 0` to zero, always.
    //
    // That would be harmless if nothing read them. `ai-chat.js:289` does:
    //
    //     LIVE MARKET INTELLIGENCE:
    //     ${JSON.stringify(contextData.market, null, 2)}
    //
    // the whole object, verbatim, into the system prompt — and eleven lines above it the same
    // prompt instructs *"For Turkish assets, consider the current inflation rate for real
    // return context."* So the model is told to reason about real returns and handed
    // **inflation = 0** for a country running **33.5 %**, which `S.settings.trInflation` has
    // held all along. Every real-return statement about a Turkish asset was judged against the
    // wrong number — and a book that holds 15.09 g of gold was told gold is worth 0.
    //
    // This is not a display defect. It is a false premise handed to the thing that writes the
    // advice, which is why it is graded above anything cosmetic.
    //
    // THE UNITS ARE IN THE KEY NAMES on purpose. A bare `inflation: 33.5` can be read as 33.5×,
    // and `goldTRY` says nothing about per-gram vs per-ounce. `S.prices.XAU` is TRY per GRAM —
    // 4375.5712 USD/oz ÷ 31.1035 × 47.89 = 6737.1, matching the stored 6737.058 to 0.1 %.
    //
    // ABSENT IS 'unavailable', NOT 0 — the vocabulary this very prompt already defines:
    // *"a field with the value 'unavailable' means SPEKTRX could not compute it this turn. It
    // does NOT mean zero."* The contract existed; this field just was not using it.
    goldTRYPerGram: _xauTRYPerGram > 0 ? +_xauTRYPerGram.toFixed(2) : 'unavailable',
    inflationPctAnnual: _trInflation > 0 ? +(_trInflation * 100).toFixed(1) : 'unavailable',
  };

  // Portfolio-level market sentiment from price changes
  const holdings = (S.holdings || []).filter(h => h && !h._del);
  let gainers = 0, losers = 0, unchanged = 0;
  const bigMovers = [];

  holdings.forEach(h => {
    if (!h.ticker || !h._priceData) return;
    const chg = h._priceData?.changePercent || 0;
    if (chg > 0.5) gainers++;
    else if (chg < -0.5) losers++;
    else unchanged++;
    if (Math.abs(chg) >= 3) {
      bigMovers.push({ ticker: h.ticker, changePct: +chg.toFixed(2) });
    }
  });

  // BIST XU100 from price state
  let xu100 = null;
  try {
    const xu = S._priceCache?.['XU100'];
    if (xu) xu100 = { price: xu.price, change: xu.changePercent };
  } catch (_e) {}

  return {
    fx,
    marketBreadth: { gainers, losers, unchanged, total: holdings.length },
    bigMovers: bigMovers.sort((a, b) => Math.abs(b.changePct) - Math.abs(a.changePct)).slice(0, 5),
    xu100,
  };
}

// ─── PUBLIC API ──────────────────────────────────────────────

/**
 * Main aggregator — fetches relevant market intelligence based on topics.
 *
 * @param {Object} topics — { crypto, news, macro, sentiment }
 * @returns {Promise<Object>} aggregated market data
 */
export async function getMarketIntel(topics = {}) {
  const all = topics.all || false;
  const results = {};

  // Always include internal context (free, no API call)
  results.internal = _getInternalContext();

  // Parallel fetch of external sources
  const promises = [];

  if (all || topics.crypto) {
    promises.push(
      _fetchCryptoGlobal().then(d => { results.cryptoGlobal = d; }),
      _fetchTrending().then(d => { results.trending = d; }),
    );
  }

  if (all || topics.sentiment) {
    promises.push(
      _fetchFearGreed().then(d => { results.fearGreed = d; }),
    );
  }

  if (all || topics.news) {
    // Build ticker list from user's holdings for relevant news
    const tickers = _getNewsTickers();
    promises.push(
      _fetchNews(tickers).then(d => { results.news = d; }),
    );
  }

  await Promise.allSettled(promises);
  return results;
}

/** Force refresh all cached data */
export async function refreshMarketIntel() {
  // Clear all cache
  Object.keys(_cache).forEach(k => delete _cache[k]);
  return getMarketIntel({ all: true });
}

/**
 * Build a concise text summary for AI context injection.
 * Call this from ai-chat.js _buildContextData when market context is needed.
 */
export async function getMarketSummary() {
  const intel = await getMarketIntel({ all: true });
  return intel;
}

// ─── HELPERS ─────────────────────────────────────────────────

/** Get top tickers from user holdings for news lookup */
function _getNewsTickers() {
  const holdings = (S.holdings || []).filter(h => h && !h._del);

  // Get unique tickers, prioritize by value
  const usTickers = holdings
    .filter(h => h.currency === 'USD' && h.type !== 'Cash' && h.type !== 'Crypto')
    .map(h => h.ticker)
    .filter(Boolean)
    .slice(0, 3);

  // Add crypto
  const cryptoTickers = holdings
    .filter(h => h.type === 'Crypto')
    .map(h => {
      // Map to Yahoo Finance crypto format
      const t = h.ticker;
      if (t === 'BTC') return 'BTC-USD';
      if (t === 'ETH') return 'ETH-USD';
      return t + '-USD';
    })
    .slice(0, 2);

  // Combine — max 5 tickers for news
  return [...usTickers, ...cryptoTickers].slice(0, 5).join(',');
}
